import React from 'react';
import {
  formatOmdDate,
  getOmdAccessories,
  getOmdFollowupProducts,
  hasOmdDescriptionHtml,
  labelOmdRelatedProduct,
  labelProductType,
  normalizeOmdDescriptionHtml,
  OMD_DESCRIPTION_HTML_CLASS,
  type OmdPanelTabId,
  type OmdProduct,
} from '../../../utils/omdProductView';
import type { OmdPanelSettings } from '../../../utils/omdPanelSettings';
import { getOmdSectionIcon, OmdPanelIcon } from '../omdPanelIcons';
import { OmdDetailGrid } from '../shared/OmdDetailGrid';
import { OmdImageCarousel } from '../shared/OmdImageCarousel';
import { OmdKpiChips } from '../shared/OmdKpiChips';
import { OmdPriceHeroCard } from '../shared/OmdPriceHeroCard';
import { OmdRawJsonSection } from '../shared/OmdRawJsonSection';
import { OmdSection } from '../shared/OmdSection';

type OmdOverviewTabProps = {
  product: OmdProduct;
  rawProduct: unknown;
  settings: OmdPanelSettings;
  onOpenLightbox: (index: number) => void;
  hideRawJson?: boolean;
};

export function OmdOverviewTab({
  product,
  rawProduct,
  settings,
  onOpenLightbox,
  hideRawJson,
}: OmdOverviewTabProps) {
  const marketingText = product.descriptions?.marketingText?.trim();
  const productDescr = product.descriptions?.productDescr?.trim();
  const shorttext1 = product.descriptions?.shorttext1?.trim();
  const shorttext2 = product.descriptions?.shorttext2?.trim();
  const hasDescription = Boolean(
    shorttext1 ||
      shorttext2 ||
      hasOmdDescriptionHtml(productDescr) ||
      hasOmdDescriptionHtml(marketingText),
  );
  const accessories = getOmdAccessories(product);
  const followupProducts = getOmdFollowupProducts(product);
  const serieRaw = product.basic?.serie;
  const serie = Array.isArray(serieRaw) ? serieRaw.filter(Boolean).join(', ') : serieRaw;

  const stammdatenRows = [
    { label: 'Artikelnr.', value: product.supplierPid },
    { label: 'GTIN', value: product.gtin != null ? String(product.gtin) : null },
    { label: 'Hersteller-ID', value: product.manufacturerId },
    { label: 'Hersteller-Nr.', value: product.manufacturerPid },
    { label: 'Artikeltyp', value: labelProductType(product.basic?.productType) },
    { label: 'Serie', value: serie },
    { label: 'Modell', value: product.basic?.modelNumber },
    { label: 'Matchcode', value: product.basic?.matchcode },
    {
      label: 'Warengruppe',
      value: product.basic?.commodityGroupDescr || product.basic?.commodityGroupId,
    },
    { label: 'Hauptwarengruppe', value: product.basic?.mainCommodityGroupDescr },
    { label: 'Gültig ab', value: formatOmdDate(product.basic?.startOfValidity) },
    { label: 'Hinweis', value: product.basic?.noteOfUse },
  ];

  return (
    <div className="space-y-4">
      <OmdImageCarousel product={product} onOpenLightbox={onOpenLightbox} />
      <OmdPriceHeroCard product={product} />

      <OmdKpiChips product={product} />

      {hasDescription && (
        <OmdSection title="Beschreibung" settings={settings}>
          <div className={`space-y-3 ${OMD_DESCRIPTION_HTML_CLASS}`}>
            {shorttext1 && <p className="font-medium text-app-text w-full">{shorttext1}</p>}
            {shorttext2 && <p className="text-app-text-muted w-full">{shorttext2}</p>}
            {hasOmdDescriptionHtml(productDescr) && (
              <div
                className={OMD_DESCRIPTION_HTML_CLASS}
                dangerouslySetInnerHTML={{ __html: normalizeOmdDescriptionHtml(productDescr!) }}
              />
            )}
            {hasOmdDescriptionHtml(marketingText) && (
              <div className="w-full">
                {hasOmdDescriptionHtml(productDescr) && (
                  <p className="text-xs font-medium text-app-text-subtle mb-1">Vermarktungstext</p>
                )}
                <div
                  className={OMD_DESCRIPTION_HTML_CLASS}
                  dangerouslySetInnerHTML={{ __html: normalizeOmdDescriptionHtml(marketingText!) }}
                />
              </div>
            )}
          </div>
        </OmdSection>
      )}

      <OmdSection
        title="Stammdaten"
        settings={settings}
        hidden={!settings.showEmptyFields && stammdatenRows.every((r) => !r.value)}
      >
        <OmdDetailGrid rows={stammdatenRows} settings={settings} />
      </OmdSection>

      {followupProducts.length > 0 && (
        <OmdSection title="Nachfolgeartikel" settings={settings}>
          <ul className="divide-y divide-app-border">
            {followupProducts.map((item, idx) => (
              <li key={idx} className="py-2 text-sm">
                <span className="text-app-text">{labelOmdRelatedProduct(item)}</span>
                <span className="mt-0.5 block text-xs text-app-text-subtle">
                  {[
                    item.supplierPid ? `Artikelnr. ${item.supplierPid}` : null,
                    item.manufacturerPid ? `Hersteller-Nr. ${item.manufacturerPid}` : null,
                    item.gtin != null ? `GTIN ${item.gtin}` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </OmdSection>
      )}

      {accessories.length > 0 && (
        <OmdSection title="Zubehör" settings={settings}>
          <ul className="divide-y divide-app-border">
            {accessories.map((acc, idx) => (
              <li key={idx} className="py-2 flex items-start justify-between gap-2 text-sm">
                <span className="text-app-text">
                  {acc.description || acc.productShortDescr || acc.manufacturerPid || acc.supplierPid || 'Zubehör'}
                </span>
                {acc.necessaryForFunction && (
                  <span className="shrink-0 text-xs font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                    erforderlich
                  </span>
                )}
              </li>
            ))}
          </ul>
        </OmdSection>
      )}

      {!hideRawJson && <OmdRawJsonSection product={rawProduct} />}
    </div>
  );
}

export function OmdFlowSection({
  id,
  title,
  settings,
  children,
  hidden,
}: {
  id: OmdPanelTabId;
  title: string;
  settings: OmdPanelSettings;
  children: React.ReactNode;
  hidden?: boolean;
}) {
  if (hidden) return null;
  const SectionIcon = getOmdSectionIcon(title, id);
  return (
    <section id={`omd-section-${id}`} className="scroll-mt-4 pt-6 first:pt-0 border-t border-app-border first:border-0">
      <h3 className="flex items-center gap-2 text-base font-semibold text-app-text mb-3">
        <OmdPanelIcon icon={SectionIcon} />
        {title}
      </h3>
      {children}
    </section>
  );
}
